import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import type { SaleDetail } from '@sellokart/shared';
import { PageHeader } from '@/components/PageHeader';
import { AsyncSection } from '@/components/AsyncSection';
import { DataTable, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import { EmptyState } from '@/components/States';
import { IconInbox } from '@/components/Icons';
import { Button } from '@/components/Button';
import { fetchSaleDetail, reportKeys } from '@/features/reports/api';
import { formatPosDateTime, saleDateTime } from '@/lib/format';
import { ApiError } from '@/lib/api';

const money = (value: number) => value.toFixed(2);

type SaleItem = SaleDetail['items'][number];
type SalePayment = SaleDetail['payments'][number];

/** A 404 here means the sale doesn't exist — never worth retrying, unlike a transient network/5xx failure. */
function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

const ITEM_COLUMNS: Column<SaleItem>[] = [
  { key: 'productName', header: 'Product', cell: (row) => row.productName },
  { key: 'quantity', header: 'Quantity', align: 'right', cell: (row) => money(row.quantity) },
  { key: 'unitPrice', header: 'Unit price', align: 'right', cell: (row) => money(row.unitPrice) },
  { key: 'total', header: 'Total', align: 'right', cell: (row) => money(row.total) },
];

const PAYMENT_COLUMNS: Column<SalePayment>[] = [
  { key: 'method', header: 'Method', cell: (row) => row.method },
  { key: 'date', header: 'Date', cell: (row) => formatPosDateTime(row.date) },
  { key: 'amount', header: 'Amount', align: 'right', cell: (row) => money(row.amount) },
];

export function SaleDetailPage() {
  const { deviceId, sourceId } = useParams<{ deviceId: string; sourceId: string }>();
  const navigate = useNavigate();
  const parsedSourceId = Number(sourceId);

  const query = useQuery({
    queryKey: reportKeys.saleDetail(deviceId ?? '', parsedSourceId),
    queryFn: ({ signal }) => fetchSaleDetail(deviceId ?? '', parsedSourceId, signal),
    enabled: Boolean(deviceId) && Number.isInteger(parsedSourceId),
    retry: (failureCount, error) => !isNotFound(error) && failureCount < 2,
  });

  if (query.isError && isNotFound(query.error)) {
    return (
      <div>
        <PageHeader title="Sale not found" />
        <EmptyState
          icon={<IconInbox className="h-5 w-5" />}
          title="This sale doesn't exist"
          description="It may have been on a different device, or the reference no longer resolves."
          action={
            <Button variant="secondary" onClick={() => navigate('/sales-history')}>
              Back to sales history
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={query.data ? `Sale ${query.data.sale.docNumber}` : 'Sale detail'}
        description={query.data ? `${query.data.sale.customerName} · ${formatPosDateTime(saleDateTime(query.data.sale))}` : undefined}
      />
      <AsyncSection
        state={query}
        skeleton={<TableSkeleton columns={4} />}
        errorTitle="Could not load this sale"
      >
        {(detail) => (
          <div className="space-y-8">
            <section className="grid grid-cols-2 gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-4">
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Status</div>
                <div className="mt-1 text-sm font-semibold text-slate-900">{detail.sale.status}</div>
              </div>
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Grand total</div>
                <div className="mt-1 text-sm font-semibold text-slate-900">{money(detail.sale.grandTotal)}</div>
              </div>
              {detail.vehicle.name !== '' || detail.vehicle.plate !== '' ? (
                <div className="col-span-2">
                  <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Vehicle</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {[detail.vehicle.name, detail.vehicle.plate, detail.vehicle.km !== '' ? `${detail.vehicle.km} km` : '']
                      .filter((part) => part !== '')
                      .join(' · ')}
                  </div>
                </div>
              ) : null}
            </section>

            <section>
              <h2 className="mb-3 text-sm font-semibold text-slate-900">Items</h2>
              {detail.items.length === 0 ? (
                <EmptyState compact title="No line items on this sale" />
              ) : (
                <DataTable
                  caption="Sale items"
                  columns={ITEM_COLUMNS}
                  rows={detail.items}
                  rowKey={(row, index) => `${row.productName}-${index}`}
                  totals={{ label: 'Total', cells: { total: money(detail.items.reduce((acc, row) => acc + row.total, 0)) } }}
                />
              )}
            </section>

            <section>
              <h2 className="mb-3 text-sm font-semibold text-slate-900">Payments</h2>
              {detail.payments.length === 0 ? (
                <EmptyState compact title="No payments recorded on this sale" />
              ) : (
                <DataTable
                  caption="Sale payments"
                  columns={PAYMENT_COLUMNS}
                  rows={detail.payments}
                  rowKey={(row, index) => `${row.method}-${index}`}
                  totals={{ label: 'Total', cells: { amount: money(detail.payments.reduce((acc, row) => acc + row.amount, 0)) } }}
                />
              )}
            </section>
          </div>
        )}
      </AsyncSection>
    </div>
  );
}
